import ReactDOM from 'react-dom';
import { useState, useEffect } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { ensureRegistrationListeners } from '@utils/pushNotificationService';
import Prompt from './Prompt.react';
import Icon from './Icon.react';
import AuthStore from '@stores/AuthStore';
import UserStore from '@stores/UserStore';
import UserActions from '@actions/UserActions';
import Analytics from '@utils/Analytics';
import store from 'store';
import './PushNotificationPrompt.scss';

const LOCALSTORAGE_KEY = 'push-notifications';
export const LOCALSTORAGE_KEY_DEVICE_TOKEN = 'push-notifications-device-key';

export const storeTokenInBackend = async (token, platform) => {
    try {
        const url = UserStore.getLinks().devices;
        const request = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json; schema=device/1' },
            body: JSON.stringify({ token, platform }),
        };
        const response = await AuthStore.fetch(url, request);
        if (!response.ok) {
            throw new Error('Failed to store token in backend.');
        }
        console.log('Token successfully stored in backend!');
    } catch (err) {
        console.error('Error storing token in backend:', err.message);
    }
};

export const updatePushInhibitedSetting = async (inhibited, enableSchedules = false) => {
    const payload = { inhibit_push_notification: inhibited };
    if (!inhibited && enableSchedules) {
        payload.daily_push_notification = true;
        payload.weekly_push_notification = true;
    }
    return UserActions.updateSpecificMeta(payload);
};

const renderDeniedContent = () => {
    const platform = Capacitor.getPlatform();

    return (
        <>
            <h2>Stay on Track with EatLove</h2>
            <p>
                You’ve turned off notifications at the system level—so you’re missing meal reminders and grocery alerts.
            </p>

            {platform === 'ios' ? (
                <p>
                    To re‑enable, go to <strong>Settings &gt; Notifications &gt; EatLove</strong> and turn on{' '}
                    <strong>Allow Notifications</strong>.
                </p>
            ) : (
                <p>
                    To re‑enable, open <strong>Settings &gt; Apps &gt; EatLove &gt; Notifications</strong> and toggle on{' '}
                    <strong>All EatLove notifications</strong>.
                </p>
            )}
        </>
    );
};

const PushNotificationPrompt = ({
    onClose = () => {},
    onPermissionResult = () => {},
    force = false,
    delay = 0,
    triggeredFrom = 'unknown',
}) => {
    const platform = Capacitor.getPlatform();
    const isMobilePlatform = platform === 'ios' || platform === 'android';
    if (!isMobilePlatform) return null;

    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [promptType, setPromptType] = useState('permission');
    const [showDontShowAgain, setShowDontShowAgain] = useState(false);
    const [hideNutrition, setHideNutrition] = useState(false);
    const [delayedVisible, setDelayedVisible] = useState(false);

    const user = UserStore.getUser();

    const handleTokenRegistration = async (token, source = 'Auto') => {
        if (!token?.value) return;

        console.log(`[${source}] New push token detected, syncing to backend...`);
        store.set(LOCALSTORAGE_KEY_DEVICE_TOKEN, token.value);
        await storeTokenInBackend(token.value, platform);

        Analytics.trackPushNotificationSyncToken({
            Action: source,
            'Device Type': platform,
        });
    };

    useEffect(() => {
        setHideNutrition(user?.preferences?.hide_nutrition || false);

        const timer = setTimeout(() => {
            setDelayedVisible(true);
        }, delay);

        return () => clearTimeout(timer);
    }, [delay]);

    useEffect(() => {
        if (!Capacitor.isPluginAvailable('PushNotifications')) {
            return;
        }

        const run = async () => {
            await ensureRegistrationListeners(
                (token) => handleTokenRegistration(token, 'Auto Sync'),
                (error) => {
                    console.error('Push registration error:', error);
                    setLoading(false);
                    setVisible(false);
                }
            );

            const storedData = store.get(LOCALSTORAGE_KEY) || { count: 0, dontShow: false, lastDismissed: null };
            const storedToken = store.get(LOCALSTORAGE_KEY_DEVICE_TOKEN);
            const { receive } = await PushNotifications.checkPermissions();

            if (!force) {
                // Apply 2-day cooldown logic before showing any prompts
                if (storedData.lastDismissed) {
                    const lastDismissedDate = new Date(storedData.lastDismissed);
                    const today = new Date();

                    const timeDifference = today.setHours(0, 0, 0, 0) - lastDismissedDate.setHours(0, 0, 0, 0);
                    const daysPassed = timeDifference / (1000 * 60 * 60 * 24);

                    if (daysPassed < 2) {
                        setVisible(false);
                        return;
                    }
                }
            }

            // ----- force flow -----
            if (force) {
                if (receive === 'denied') {
                    setPromptType('denied');
                    setVisible(true);
                    return;
                }

                if (receive === 'prompt' || receive === 'prompt-with-rationale') {
                    requestPushNotifications();
                    return;
                }

                if (receive === 'granted') {
                    await PushNotifications.register();
                    await updatePushInhibitedSetting(false, true);
                    onPermissionResult(true);
                    setVisible(false);
                    return;
                }
            }

            // ----- normal flow -----
            if (storedData.dontShow) {
                setVisible(false);
                return;
            }

            if (storedToken) {
                if (receive === 'granted' && storedToken) {
                    setVisible(false);
                    return;
                }

                if (receive === 'denied') {
                    setPromptType('denied');
                    setVisible(true);
                    return;
                }

                setPromptType('permission');
                setVisible(true);
                return;
            }

            // Show "Don't Show Again" after two dismissals
            if (storedData.count >= 2) {
                setShowDontShowAgain(true);
            }

            store.set(LOCALSTORAGE_KEY, { ...storedData, count: storedData.count + 1 });

            setPromptType('permission');
            setVisible(true);
        };

        run();
    }, [platform, force]);

    useEffect(() => {
        if (!Capacitor.isPluginAvailable('PushNotifications')) {
            return;
        }

        const checkPermissionOnResume = async () => {
            const { receive } = await PushNotifications.checkPermissions();

            Analytics.trackPushNotificationPromptResumed({
                'Opt-In Status': receive === 'granted' ? 'Enabled' : 'Denied',
                'Device Type': platform,
            });

            if (receive !== 'denied') {
                await ensureRegistrationListeners(
                    (token) => handleTokenRegistration(token, 'Settings Sync'),
                    (err) => console.error('Post-settings registration error', err)
                );
                await PushNotifications.register();
                setVisible(false);
                await updatePushInhibitedSetting(false);
                onPermissionResult(true);
            } else {
                await updatePushInhibitedSetting(true);
            }
        };

        const removeAppStateListener = App.addListener('appStateChange', ({ isActive }) => {
            if (isActive && visible && promptType === 'denied') {
                checkPermissionOnResume();
            }
        });

        return () => {
            removeAppStateListener.remove();
        };
    }, [visible, promptType]);

    useEffect(() => {
        if (visible && delayedVisible) {
            Analytics.trackPushNotificationPromptShown({
                'Triggered From': triggeredFrom,
                'Prompt Type': promptType,
                'Device Type': platform,
            });
        }
    }, [visible, delayedVisible, triggeredFrom, promptType, platform]);

    const closePrompt = (action = false, dontShowAgain = false) => {
        const storedData = store.get(LOCALSTORAGE_KEY) || { count: 0, dontShow: false, lastDismissed: null };

        if (action === 'Not Now') {
            storedData.lastDismissed = new Date().toISOString();
        }

        if (dontShowAgain) {
            storedData.dontShow = true;
        }

        store.set(LOCALSTORAGE_KEY, storedData);

        setVisible(false);
        setLoading(false);
        onClose();

        if (action) {
            Analytics.trackPushNotificationPromptClosed({
                Action: action,
                'Device Type': platform,
            });
        }
    };

    const requestPushNotifications = async () => {
        if (!Capacitor.isPluginAvailable('PushNotifications')) {
            console.warn('PushNotifications plugin is not available on this platform.');
            return;
        }
        setLoading(true);

        Analytics.trackPushNotificationPromptAccepted({
            'Triggered From': triggeredFrom,
            'Device Type': platform,
        });

        try {
            ensureRegistrationListeners(
                (token) => handleTokenRegistration(token, 'Manual Prompt'),
                (error) => console.error('Push registration error:', error)
            );
            const permStatus = await PushNotifications.requestPermissions();

            if (permStatus.receive === 'granted') {
                Analytics.trackPushNotificationPermissionGranted({
                    'Opt-In Status': 'Enabled',
                    'Device Type': platform,
                });
            } else {
                Analytics.trackPushNotificationPermissionDenied({
                    'Opt-In Status': 'Denied',
                    'Device Type': platform,
                });
            }

            if (permStatus.receive !== 'granted') {
                console.log('Push notifications permission denied.');
                await updatePushInhibitedSetting(true);
                onPermissionResult(false);
                setLoading(false);
                setVisible(false);
                return;
            }
            await updatePushInhibitedSetting(false, true);
            await PushNotifications.register();
            onPermissionResult(true);
            closePrompt();
        } catch (error) {
            console.error('Error in push notification setup:', error);
            onPermissionResult(false);
            setLoading(false);
            setVisible(false);
            throw error;
        }
    };

    if (!visible || !delayedVisible) return null;

    return ReactDOM.createPortal(
        <div className="el-push-notification-prompt">
            {promptType === 'permission' ? (
                <Prompt
                    buttons={[
                        {
                            label: loading ? 'Enabling ' : 'Enable Notifications',
                            onClick: requestPushNotifications,
                            variant: 'default',
                            disabled: loading,
                            format: 'wide',
                            size: 'large',
                            isLoading: loading,
                        },
                        {
                            label: 'Not Now',
                            onClick: () => closePrompt('Not Now'),
                            variant: 'outline',
                            format: 'wide',
                            size: 'large',
                            disabled: loading,
                        },
                        showDontShowAgain &&
                            !force && {
                                label: 'Don’t Show Again',
                                onClick: () => closePrompt('Don’t Show Again', true),
                                variant: 'outline',
                                className: 'el-btn el-large-btn el-raspberry-outline-btn link',
                                format: 'wide',
                                size: 'large',
                                disabled: loading,
                            },
                    ].filter(Boolean)}
                    onClose={() => closePrompt('App Close')}
                >
                    {loading ? (
                        <div className="loading">
                            <Icon name="spinner2" />
                        </div>
                    ) : (
                        <>
                            <h2>Stay on Track with Meal Reminders</h2>
                            <p>Enable notifications to help you achieve your nutrition goals:</p>
                            <ul>
                                <li>
                                    <span className="icon">
                                        <Icon name="messages"></Icon>
                                    </span>
                                    <span className="text">
                                        Daily menu reminders to get you excited about what’s cooking
                                    </span>
                                </li>
                                <li>
                                    <span className="icon">
                                        <Icon name="schedule3"></Icon>
                                    </span>
                                    <span className="text">
                                        Weekly meal planning and grocery reminders to save time
                                    </span>
                                </li>
                                {!hideNutrition && (
                                    <li>
                                        <span className="icon">
                                            <Icon name="meal-plan"></Icon>
                                        </span>
                                        <span className="text">Meal logging reminders to track your progress</span>
                                    </li>
                                )}
                            </ul>
                        </>
                    )}
                </Prompt>
            ) : (
                <Prompt
                    buttons={[
                        {
                            label: 'Not Now',
                            onClick: () => closePrompt('Not Now'),
                            variant: 'outline',
                            format: 'wide',
                            size: 'large',
                        },
                        showDontShowAgain &&
                            !force && {
                                label: 'Don’t Show Again',
                                onClick: () => closePrompt('Don’t Show Again', true),
                                variant: 'outline',
                                className: 'el-btn el-large-btn el-raspberry-outline-btn link',
                                format: 'wide',
                                size: 'large',
                            },
                    ].filter(Boolean)}
                    onClose={() => closePrompt('App Close')}
                >
                    {renderDeniedContent()}
                </Prompt>
            )}
        </div>,
        document.getElementById('root')
    );
};

export default PushNotificationPrompt;
